import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { DataTableColumn, PaginatorStatus, SortConfiguration } from '../../generics/data-table/data-table.component';
import { ErrorResponse } from 'src/app/models/error.model';
import { TipoFianza, TipoProducto } from 'src/app/models/tipo-fianza.model';
import { TipoDescuento } from 'src/app/models/tipo-descuento.model';
import { Zona } from 'src/app/models/zona.model';
import { TiposFianzaService } from 'src/app/services/tipos-fianza.service';
import { TiposDescuentoService } from 'src/app/services/tipos-descuento.service';
import { ZonasService } from 'src/app/services/zonas.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
    selector: 'app-tipos-fianza',
    templateUrl: './tipos-fianza.component.html',
    styleUrls: ['./tipos-fianza.component.scss']
})
export class TiposFianzaComponent implements OnInit, OnChanges {

    @Input() embedded = false;
    @Input() tipoProductoInicial?: TipoProducto;

    @ViewChild('plazoColumnTemplate', { static: true })
    plazoColumnTemplate: TemplateRef<any>;

    @ViewChild('zonaColumnTemplate', { static: true })
    zonaColumnTemplate: TemplateRef<any>;

    @ViewChild('activoColumnTemplate', { static: true })
    activoColumnTemplate: TemplateRef<any>;

    @ViewChild('actionsColumnTemplate', { static: true })
    actionsColumnTemplate: TemplateRef<any>;

    paginatorStatus: PaginatorStatus = {
        itemsPerPage: 50,
        page: 0,
        totalElementCount: 0
    };

    columns: DataTableColumn[] = [
        { name: 'Nombre', prop: 'nombre', sortable: true },
        { name: 'Clave', prop: 'clave', width: '130px' },
        { name: 'Plazo', width: '120px' },
        { name: 'Coef./mes', prop: 'coeficientePrimaMensual', width: '95px', parseValue: (v: number) => `${v}%` },
        { name: 'Comisión', prop: 'comision', width: '90px', parseValue: (v: number) => `${v}%` },
        { name: 'Zona', width: '120px' },
        { name: 'Estado', width: '90px' },
        { name: '', width: '90px' }
    ];

    tipoProductoActual: TipoProducto = 'VIVIENDA';
    selectedZonaId: number | null = null;
    sortConfiguration?: SortConfiguration;
    tiposFianza: TipoFianza[] = [];
    zonas: Zona[] = [];
    tiposDescuento: TipoDescuento[] = [];

    get displayTiposFianza(): (TipoFianza & { heredado?: boolean })[] {
        if (this.selectedZonaId === null) {
            return this.tiposFianza.filter(f => !f.idZona);
        }
        const defaults = this.tiposFianza.filter(f => !f.idZona && f.activo);
        const zoneItems = this.tiposFianza.filter(f => f.idZona === this.selectedZonaId && f.activo);
        const merged = defaults.map(def => {
            const override = zoneItems.find(z => z.clave === def.clave);
            return override ?? { ...def, heredado: true };
        });
        const orphans = zoneItems.filter(zi => !defaults.find(d => d.clave === zi.clave));
        return [...merged, ...orphans];
    }

    constructor(
        private tiposFianzaService: TiposFianzaService,
        private tiposDescuentoService: TiposDescuentoService,
        private zonasService: ZonasService,
        private utilsService: UtilsService
    ) { }

    ngOnInit(): void {
        if (this.tipoProductoInicial) {
            this.tipoProductoActual = this.tipoProductoInicial;
        }
        this.columns.find(col => col.name === 'Plazo').template = this.plazoColumnTemplate;
        this.columns.find(col => col.name === 'Zona').template = this.zonaColumnTemplate;
        this.columns.find(col => col.name === 'Estado').template = this.activoColumnTemplate;
        this.columns.find(col => col.name === '').template = this.actionsColumnTemplate;
        this.loadZonas();
        this.loadTiposDescuento();
        this.getTiposFianza();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tipoProductoInicial'] && !changes['tipoProductoInicial'].firstChange) {
            this.tipoProductoActual = this.tipoProductoInicial;
            this.selectedZonaId = null;
            this.paginatorStatus.page = 0;
            this.getTiposFianza();
        }
    }

    loadZonas() {
        this.zonasService.getZonas(0, 200, [], { 'ocultar-inactivos': 'false' }).subscribe({
            next: response => { this.zonas = response.body ?? []; }
        });
    }

    loadTiposDescuento() {
        this.tiposDescuentoService.getTiposDescuento(0, 200, [], { 'ocultar-inactivos': 'false' }).subscribe({
            next: response => { this.tiposDescuento = response.body ?? []; }
        });
    }

    getZonaLabel(idZona?: number | null): string {
        if (!idZona) return 'Default';
        return this.zonas.find(z => z.id === idZona)?.nombre ?? `Zona ${idZona}`;
    }

    getTiposDescuentoLabel(ids: number[]): string {
        if (!ids || ids.length === 0) return '—';
        return ids
            .map(id => this.tiposDescuento.find(t => t.id === id)?.nombre ?? `#${id}`)
            .join(', ');
    }

    getTiposFianza() {
        this.tiposFianzaService.getTiposFianza(
            this.paginatorStatus.page,
            this.paginatorStatus.itemsPerPage,
            this.sortConfiguration ? [this.sortConfiguration] : [],
            this.tipoProductoActual,
            false
        ).subscribe({
            next: response => {
                this.paginatorStatus.totalElementCount = parseInt(response.headers.get('pagination-total-count') ?? '0', 10);
                this.tiposFianza = response.body;
            },
            error: (error: ErrorResponse) => this.utilsService.alert('Error al obtener tipos de fianza', error.error?.descripcion)
        });
    }

    onTipoChange(tipo: TipoProducto) {
        this.tipoProductoActual = tipo;
        this.paginatorStatus.page = 0;
        this.getTiposFianza();
    }

    onZonaChange(idZona: number | null) {
        this.selectedZonaId = idZona;
    }

    revertirADefault(tipoFianza: TipoFianza) {
        const dialog = this.utilsService.confirm(
            '¿Revertir este tipo de fianza al Default?',
            `"${tipoFianza.nombre}" pasará a la configuración base (sin zona específica).`
        );
        dialog.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.tiposFianzaService.updateTipoFianza({ ...tipoFianza, idZona: null }).subscribe({
                    next: () => {
                        this.utilsService.alert('Fianza revertida', `"${tipoFianza.nombre}" ahora pertenece al Default.`);
                        this.getTiposFianza();
                    },
                    error: (error: ErrorResponse) => this.utilsService.alert('Error al revertir', error.error?.descripcion)
                });
            }
        });
    }

    updatePage(event: PageEvent) {
        this.paginatorStatus.itemsPerPage = event.pageSize;
        this.paginatorStatus.page = event.pageIndex;
        this.getTiposFianza();
    }

    updateSort(event: SortConfiguration) {
        this.sortConfiguration = event;
        this.getTiposFianza();
    }

    deleteTipoFianza(tipoFianza: TipoFianza) {
        const dialog = this.utilsService.confirm(
            '¿Dar de baja este tipo de fianza?',
            `"${tipoFianza.nombre}" quedará inactivo para ${tipoFianza.tipoProducto === 'VIVIENDA' ? 'Vivienda' : 'Comercial'}.`
        );
        dialog.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.tiposFianzaService.deleteTipoFianza(tipoFianza.id).subscribe({
                    next: () => {
                        this.utilsService.alert('Tipo de fianza dado de baja', `"${tipoFianza.nombre}" fue dado de baja correctamente.`);
                        this.getTiposFianza();
                    },
                    error: (error: ErrorResponse) => this.utilsService.alert('Error al dar de baja', error.error?.descripcion)
                });
            }
        });
    }
}
